/**
 * Runtime configuration injected by the container entrypoint (`docker/docker-entrypoint.sh`)
 * before nginx starts. The entrypoint writes `window.__RUNTIME_CONFIG__` into
 * `public/config.js` from the container's environment variables, and `index.html` loads that
 * script ahead of the app bundle. The same build artifact is reused across environments —
 * only the container's env vars differ per deployment (dev/staging/prod, later a Helm values
 * file).
 *
 * `public/config.js` ships an empty object for local dev (`npm run dev` / `vite preview`), so
 * every field below falls back to its default in `@/config/api.ts` / `@/config/keycloak_config.ts`
 * there. All values arrive as strings (env vars have no other shape); callers that need a
 * number convert with `Number(runtimeConfig.FIELD ?? default)`.
 */

interface RuntimeConfig {
  API_BASE_URL?: string
  // Backend base URL. All TanStack API calls are prefixed with this.

  KEYCLOAK_URL?: string
  // Keycloak base URL (issuer origin, login/token endpoints).

  KEYCLOAK_HEALTH_CHECK_URL?: string
  // Keycloak management endpoint polled by useKeycloakHealth to gate the login redirect.

  KEYCLOAK_HEALTH_CHECK_INTERVAL_SECONDS?: string
  // Seconds between Keycloak health checks. Currently unused (defined for future wiring).

  KEYCLOAK_REALM?: string
  // Keycloak realm name.

  KEYCLOAK_CLIENT_ID?: string
  // Keycloak client ID the frontend authenticates as.

  CLIENTS_PAGE_LIMIT?: string
  // Page size for the clients list (infinite scroll).

  CLIENTS_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the clients list.

  PAYMENTS_PAGE_LIMIT?: string
  // Page size for the payments list (infinite scroll).

  PAYMENTS_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the payments list.

  PAYMENT_CARD_UNIDENTIFIED_POLLING_INTERVAL_MS?: string
  // Payment card polling interval (ms) while the payment is not yet identified.

  PAYMENT_CARD_IDENTIFIED_POLLING_INTERVAL_MS?: string
  // Payment card polling interval (ms) once the payment is identified.

  SERVICE_CATEGORIES_PAGE_LIMIT?: string
  // Page size for the service categories list (infinite scroll).

  SERVICE_CATEGORIES_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the service categories list.

  SUBSCRIPTION_TYPES_PAGE_LIMIT?: string
  SUBSCRIPTIONS_PAGE_LIMIT?: string
  // Page size for the subscription types list (infinite scroll).

  SUBSCRIPTION_TYPES_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the subscription types list.

  SERVICE_TYPES_PAGE_LIMIT?: string
  // Page size for the service types list (infinite scroll).

  SERVICE_TYPES_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the service types list.

  BUSINESS_SERVICE_TYPE_CARD_POLLING_INTERVAL_MS?: string
  // Polling interval (ms) for every query inside the business service type info card.

  BUSINESS_SERVICES_BY_TYPE_PAGE_LIMIT?: string
  // Page size for the per-type business services stream (/business-services/types/{id}).

  BUSINESS_SERVICES_PAGE_LIMIT?: string
  // Page size for the business services list (infinite scroll).

  BUSINESS_SERVICES_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the business services list.

  CLIENT_VISIT_COUNTS_PAGE_LIMIT?: string
  // Page size for the client-visit-counts-by-period stream (Finances tab drains every page).

  PERFORMER_STATS_PAGE_LIMIT?: string
  // Page size for the performer-stats-by-period stream (Stuff tab drains every page).

  PERFORMERS_PAGE_LIMIT?: string
  // Page size for the performers list (infinite scroll).

  PERFORMERS_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the performers list.

  RULES_PAGE_LIMIT?: string
  // Page size for the rules list (infinite scroll).

  RULES_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the rules list.

  DISEASES_PAGE_LIMIT?: string
  // Page size for the diseases list (infinite scroll).

  DISEASES_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the diseases list.

  CLIENT_PAYMENTS_PAGE_LIMIT?: string
  // Page size for the per-client payments stream (/payments/clients/{id}).

  CLIENT_BUSINESS_SERVICES_PAGE_LIMIT?: string
  // Page size for the per-client business services stream (/business-services/clients/{id}).

  CLIENT_BALANCES_PAGE_LIMIT?: string
  // Page size for the per-client balances stream (/balances/person/{id}).

  CLIENT_DISEASES_PAGE_LIMIT?: string
  // Page size for the per-client diseases stream (/diseases/clients/{id}).

  CLIENT_CARD_POLLING_INTERVAL_MS?: string
  // Polling interval (ms) for every client-info-card query (person + four list streams).

  BOOKING_TYPES_PAGE_LIMIT?: string
  // Page size for the booking types list (fully drained for the Types tab / room sub-tabs).

  BOOKING_TYPES_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the booking types list.

  BOOKING_SCHEDULE_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the weekly booking schedule grid.

  GROUPS_PAGE_LIMIT?: string
  // Page size for the groups list (Clients tab's "Группы" pane).

  GROUPS_POLLING_INTERVAL_MS?: string
  // Auto-refresh interval (ms) for the groups list.
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig
  }
}

const runtimeConfig: RuntimeConfig = window.__RUNTIME_CONFIG__ ?? {}

export default runtimeConfig
